/**
 * WHY A SUBSCRIPTION ITEM WAS NOT ADDED — in Hebrew, with the next step and a fault reference.
 * ============================================================================================
 *
 * THE FIELD FINDING OF 2026-10-20. The Owner added a product to a subscription in the installed
 * Staging console and saw:
 *
 *     הפעולה לא בוצעה — השרת סירב לבקשה ולא נשמר שינוי
 *
 * The server had refused with a specific, actionable reason. `platform.subscription.sku.add` is a
 * SALE, and **F1R-03** refuses one it cannot price — naming whether there is no published price
 * book at all, whether this SKU has no price in the applicable book, whether its price starts
 * later, or whether no band covers the quantity, AND which book was consulted. None of those four
 * reasons was mapped anywhere in this client, so all of them arrived as one sentence that sends
 * the reader nowhere.
 *
 * ── WHAT THIS MODULE IS, AND THE RULE IT KEEPS ──────────────────────────────────────────────────
 *
 * It is the mapper `actionOutcome.ts` requires every screen to have: *"no screen renders a member
 * of a body"*. The server's reason code is never shown; what is shown is a sentence, what did NOT
 * happen, and the one next step that resolves it.
 *
 * AND IT SAYS WHAT WAS NOT WRITTEN, EVERY TIME. Every refusal here is returned by the service
 * BEFORE the insert (`services/provisioning.ts#addSku` reads the book, resolves the price and only
 * then writes), with one exception named below — `concurrent_sku_change`, where another request got
 * there first. An operator who does not know whether half of it persisted will go looking in the
 * database, which is the behaviour 08 §7 exists to prevent.
 *
 * THE FAULT REFERENCE IS THE `requestUid`, not an invented ticket number: it is what the audit row
 * and the server log are keyed by, so quoting it back is what makes a refusal traceable. It is NOT
 * an error — a priced-sale refusal is a correct answer — so it is presented as a reference, not as
 * an apology.
 */

import type { ConflictBody } from './client';

export interface SubscriptionItemRefusalText {
  readonly titleHe: string;
  readonly explanationHe: string;
  /** What is NOT in the database because of this refusal. */
  readonly unchangedHe: string;
  readonly nextStepHe: string;
  readonly tone: 'warn' | 'error' | 'muted';
}

/**
 * THE FOUR PRICING REFUSALS, KEPT APART.
 *
 * They look alike and they send the operator to four different places: publish a book, price the
 * SKU in the book that is in force, wait for (or correct) a future effective date, or add a band
 * that covers the quantity asked for. Collapsing them into "no price" would be the same loss of
 * information this file was written to repair, one level in.
 */
const REFUSAL_HE: Readonly<Record<string, SubscriptionItemRefusalText>> = {
  no_published_price_book: {
    titleHe: 'אין מחירון בתוקף, ולכן אין במה לתמחר את הפריט',
    explanationHe:
      'הוספת פריט למנוי היא מכירה, ומכירה נרשמת עם מחיר, מטבע והמחירון שממנו נלקחו. ' +
      'לפלטפורמה אין כרגע מחירון מפורסם ואין מחירון מוצמד ללקוח הזה, ולכן אין מחיר להצמיד לפריט.',
    unchangedHe: 'הפריט לא נוסף, המנוי לא שונה ולא נרשמה מכירה.',
    nextStepHe:
      'באזור המחירונים: יצירת מחירון, קביעת מחיר לפריט ופרסום המחירון — ואז הוספת הפריט שוב. ' +
      'אם הפריט אמור להיות כלול בחבילה ולא להימכר בנפרד, המסלול הוא הכללתו בתכנית והחלת הרכב התכנית על המנוי.',
    tone: 'warn',
  },
  sku_not_priced: {
    titleHe: 'לפריט הזה אין מחיר במחירון שבתוקף',
    explanationHe:
      'המחירון קיים, והפריט אינו מופיע בו. מכירה ללא מחיר מוסכם נדחית לפני הכתיבה כדי שלא ' +
      'ייווצר פריט שהלקוח מחזיק ושלפלטפורמה אין עליו סכום.',
    unchangedHe: 'הפריט לא נוסף, המנוי לא שונה ולא נרשמה מכירה.',
    nextStepHe:
      'הוספת שורת מחיר לפריט במחירון שמופיע כאן, ואז הוספת הפריט למנוי שוב. ' +
      'פריט שכלול בחבילה אינו צריך מחיר — הכללה בתכנית היא המסלול שלו.',
    tone: 'warn',
  },
  sku_not_yet_priced: {
    titleHe: 'המחיר של הפריט נכנס לתוקף במועד עתידי',
    explanationHe:
      'קיימת שורת מחיר לפריט, ותאריך התחולה שלה מאוחר מהיום. מחיר הוא שורה מתוארכת, ' +
      'וכדי שלא יתומחרו תקופות שנסגרו אין משתמשים בשורה שטרם נכנסה לתוקף.',
    unchangedHe: 'הפריט לא נוסף, המנוי לא שונה ולא נרשמה מכירה.',
    nextStepHe:
      'הוספת הפריט במועד התחולה, או תיקון תאריך התחולה במחירון אם הוא הוזן בטעות.',
    tone: 'warn',
  },
  no_applicable_price_band: {
    titleHe: 'אין מדרגת מחיר שמכסה את הכמות הזאת',
    explanationHe:
      'לפריט יש מחיר במחירון, והמדרגות הקיימות אינן כוללות את הכמות שהוזנה. ' +
      'השרת אינו בוחר מדרגה קרובה ואינו מחשב מחיר בעצמו.',
    unchangedHe: 'הפריט לא נוסף, המנוי לא שונה ולא נרשמה מכירה.',
    nextStepHe:
      'הזנת כמות שנמצאת בתוך מדרגה קיימת, או הוספת מדרגה שמכסה את הכמות הזאת במחירון.',
    tone: 'warn',
  },

  /* ── NOT PRICING. State and input, each with its own address. ─────────────────────────────── */

  subscription_not_found: {
    titleHe: 'ללקוח אין מנוי פעיל שאפשר להוסיף לו פריט',
    explanationHe:
      'פריט נוסף למנוי חי. ללקוח הזה אין מנוי במצב שמקבל רכישות חדשות — ייתכן שההקמה לא הושלמה, ' +
      'או שהמנוי מושעה או סגור.',
    unchangedHe: 'לא נוצר מנוי ולא נוסף פריט.',
    nextStepHe: 'המשך הקמת הלקוח, או בדיקת מצב המנוי בכרטיס הלקוח לפני ניסיון נוסף.',
    tone: 'muted',
  },
  tenant_not_found: {
    titleHe: 'הלקוח הזה אינו קיים',
    explanationHe: 'הכתובת מציינת לקוח שאינו במערכת, ולכן אין מנוי להוסיף לו פריט.',
    unchangedHe: 'שום דבר לא נכתב.',
    nextStepHe: 'חזרה לרשימת הלקוחות ובחירת לקוח מתוכה.',
    tone: 'muted',
  },
  invalid_quantity: {
    titleHe: 'הכמות אינה מספר שלם חיובי',
    explanationHe:
      'כמות של אפס אינה הסרה של פריט, והסרה היא פעולה אחרת עם הרשאה אחרת. ' +
      'לכן כמות שאינה מספר שלם מ־1 ומעלה נדחית ולא מתפרשת.',
    unchangedHe: 'הפריט לא נוסף והמנוי לא שונה.',
    nextStepHe: 'הזנת כמות שלמה מ־1 ומעלה. להסרת פריט קיים יש פעולה נפרדת.',
    tone: 'warn',
  },
  invalid_sku_code: {
    titleHe: 'לא נבחר פריט',
    explanationHe: 'לא הגיע קוד פריט לשרת, ולכן לא היה מה להוסיף.',
    unchangedHe: 'המנוי לא שונה.',
    nextStepHe: 'בחירת פריט מתוך הרשימה ושליחה מחדש.',
    tone: 'muted',
  },
  concurrent_sku_change: {
    titleHe: 'שינוי אחר על אותו פריט הגיע קודם',
    explanationHe:
      'בקשה אחרת שינתה את אותה שורת פריט באותו רגע. כדי שלא תיכתב כמות על גבי שינוי שלא נראה, ' +
      'הבקשה הזאת נדחתה — וזה המצב היחיד כאן שבו הכתיבה נעצרה אחרי שהתחילה.',
    unchangedHe: 'השינוי שלך לא נשמר. מה שקיים במנוי הוא מה שהבקשה האחרת כתבה.',
    nextStepHe: 'רענון המסך, קריאת מצב המנוי כפי שהוא עכשיו, והחלטה אם עוד נדרש שינוי.',
    tone: 'warn',
  },

  /* ── AND THE PLAN-COMPOSITION ROUTE, which shares this mapper because the operator does not
       experience it as a different subject. ─────────────────────────────────────────────────── */

  plan_includes_nothing: {
    titleHe: 'התכנית של הלקוח אינה כוללת שום מוצר',
    explanationHe:
      'התכנית שהמנוי רשום עליה לא כוללת אף מוצר, ולכן אין זכאות להחיל. ' +
      'הכללת מוצרים בתכנית נעשית בקטלוג, והיא חלה על כל לקוח שתכניתו היא זו.',
    unchangedHe: 'המנוי לא שונה ולא נוספה זכאות.',
    nextStepHe: 'בקטלוג: הכללת המוצרים בתכנית הזאת, ואז החלת הרכב התכנית על המנוי.',
    tone: 'muted',
  },
};

const GENERIC: SubscriptionItemRefusalText = {
  titleHe: 'הפעולה על המנוי לא בוצעה',
  explanationHe:
    'השרת סירב לבקשה. הסירוב נעשה לפני הכתיבה, ולכן לא נשמר שינוי — אבל לגרסה הזאת אין נוסח ' +
    'בעברית לסיבה שהשרת נתן, ולכן אין כאן צעד מדויק.',
  unchangedHe: 'המנוי לא שונה.',
  nextStepHe: 'רענון המסך וקריאת מצב המנוי. אם הסירוב חוזר, יש להעביר את האסמכתה שלמטה לבדיקה.',
  tone: 'error',
};

/** Exposed for the mirror test only: every reason this build has Hebrew for. */
export function mappedSubscriptionItemRefusals(): readonly string[] {
  return Object.keys(REFUSAL_HE);
}

/**
 * The refusal, in Hebrew, with the book named when the server named one.
 *
 * The book's uid is appended to the NEXT STEP rather than to the explanation, because that is where
 * it is used: it tells the operator which of possibly two books — a pinned negotiated one or the
 * published list — they have to go and change.
 */
export function subscriptionItemRefusalText(body: ConflictBody): SubscriptionItemRefusalText {
  const base = REFUSAL_HE[body.error] ?? GENERIC;
  if (body.priceBookUid === undefined) return base;
  return {
    ...base,
    nextStepHe: `${base.nextStepHe} המחירון שנבדק: ${body.priceBookUid}.`,
  };
}
